import { BaseError, ContractFunctionRevertedError, UserRejectedRequestError, parseAbi } from 'viem';

/** Custom errors of the v3.7 pool and of this market's oracle adapter, so that viem can decode revert data. */
export const errorsAbi = parseAbi([
  'error CollateralCannotCoverNewBorrow()',
  'error HealthFactorLowerThanLiquidationThreshold()',
  'error LtvValidationFailed()',
  'error BorrowingNotEnabled()',
  'error SupplyCapExceeded()',
  'error BorrowCapExceeded()',
  'error ReserveFrozen()',
  'error ReservePaused()',
  'error ReserveInactive()',
  'error NotEnoughAvailableUserBalance()',
  'error InvalidAmount()',
  'error NoDebtOfSelectedType()',
  'error UnderlyingBalanceZero()',
  'error UserInIsolationModeOrLtvZero()',
  'error CollateralBalanceIsZero()',
  'error FeedDead(uint256 age, uint256 hardMaxAge)',
  'error InvalidPrice(int256 answer)',
  'error ERC20InsufficientBalance(address sender, uint256 balance, uint256 needed)',
  'error ERC20InsufficientAllowance(address spender, uint256 allowance, uint256 needed)',
]);

const NO_POWER = 'No usable borrowing power: you have no collateral, or one of your collaterals is at LTV 0 right now (market closed or frozen), which blocks new borrowing and some withdrawals.';
const KNOWN: Record<string, string> = {
  CollateralCannotCoverNewBorrow: 'The amount exceeds what your collateral allows (max LTV).',
  HealthFactorLowerThanLiquidationThreshold: 'This would push your health factor below 1.',
  LtvValidationFailed: NO_POWER,
  UserInIsolationModeOrLtvZero: NO_POWER,
  BorrowingNotEnabled: 'This asset cannot be borrowed.',
  SupplyCapExceeded: 'The supply cap of this reserve is reached.',
  BorrowCapExceeded: 'The borrow cap of this reserve is reached.',
  ReserveFrozen: 'This reserve is frozen: no new supplies or borrows.',
  ReservePaused: 'This reserve is paused.',
  ReserveInactive: 'This reserve is not active.',
  NotEnoughAvailableUserBalance: 'Not enough balance available.',
  InvalidAmount: 'Invalid amount.',
  NoDebtOfSelectedType: 'There is no debt to repay.',
  UnderlyingBalanceZero: 'Balance is zero.',
  CollateralBalanceIsZero: 'Balance is zero.',
  FeedDead: 'A price feed has not been updated for over 7 days, so positions cannot be valued. The price keeper needs attention.',
  InvalidPrice: 'The oracle returned an invalid price.',
  ERC20InsufficientBalance: 'Not enough tokens in your wallet.',
  ERC20InsufficientAllowance: 'Token allowance is too low.',
};

/** Turns wallet, RPC and contract errors into one readable sentence. */
export function explainError(e: unknown): string {
  if (e instanceof BaseError) {
    if (e.walk((x) => x instanceof UserRejectedRequestError)) return 'You cancelled the transaction.';
    const revert = e.walk((x) => x instanceof ContractFunctionRevertedError);
    if (revert instanceof ContractFunctionRevertedError) {
      const name = revert.data?.errorName;
      if (name && KNOWN[name]) return KNOWN[name];
      return `The transaction would revert: ${name ?? revert.reason ?? revert.shortMessage}`;
    }
    if (/insufficient funds/i.test(e.message)) return 'Not enough test ETH for gas. Get some from the faucet.';
    return e.shortMessage;
  }
  if ((e as { code?: number } | null)?.code === 4001) return 'You cancelled the transaction.';
  return e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200);
}
